import React from 'react';
import KPICard, { KPIGrid } from '../KPICard';
import BarChart from '../charts/BarChart';
import DataTable from '../DataTable';
import { formatInt, formatPct } from '../../utils/format';

const NA = 'Not in the data room';

function titleCase(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Subtitle from package meta (no deal-specific copy baked in). */
export function landSubtitle(meta = {}) {
  const counties = (meta.counties || []).map(titleCase).filter(Boolean);
  const parts = [];
  if (counties.length) parts.push(`${counties.join(' & ')} ${counties.length > 1 ? 'Counties' : 'County'}`);
  if (meta.basin) parts.push(meta.basin);
  if (meta.seller) parts.push(meta.seller);
  return parts.join(' · ');
}

const num = (v) => (Number.isFinite(Number(v)) && v !== null ? Number(v) : null);
const intOr = (v) => (num(v) == null ? '—' : formatInt(Math.round(num(v))));

export default function Land({ data }) {
  const kpis = data?.kpis || {};
  const units = Array.isArray(data?.units) ? data.units : [];
  const ops = data?.ops || null;
  const marketing = data?.marketing || null;
  const topUnits = units.slice(0, 15);

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <div className="page-header-left">
          <h2 className="page-title">Land, Ops & Marketing</h2>
          <p className="page-subtitle">{landSubtitle(data?.meta)}</p>
          <div className="page-quote">
            <span className="page-quote-label">{data.quoteLabel}</span>
            <span>&ldquo;{data.quote}&rdquo;</span>
          </div>
        </div>
      </div>

      <KPIGrid>
        <KPICard
          label="Gross acres"
          value={intOr(kpis.grossAcres)}
          sub={num(kpis.netAcres) == null ? NA : `Net ${intOr(kpis.netAcres)}`}
          color="var(--navy)"
        />
        <KPICard
          label="HBP"
          value={num(kpis.hbpPct) == null ? '—' : formatPct(kpis.hbpPct * 100)}
          sub={num(kpis.hbpNet) == null ? NA : `${intOr(kpis.hbpNet)} net HBP acres`}
          color="var(--green)"
        />
        <KPICard label="Non-HBP" value={intOr(kpis.nonHbpNet)} sub="Net acres" color="var(--amber)" />
        <KPICard
          label="Units / tracts"
          value={formatInt(units.length)}
          sub="In acreage summary"
          color="var(--teal)"
        />
      </KPIGrid>

      {units.length === 0 ? (
        <section className="dashboard-section">
          <div className="chart-card">
            <div className="chart-title">Acreage</div>
            <p className="chart-subtitle">
              No acreage / unit schedule in the data room yet — request a land schedule from the seller.
            </p>
          </div>
        </section>
      ) : (
        <section className="dashboard-section">
          <div className="chart-card">
            <div className="chart-title">Net acres by unit (top 15)</div>
            <div className="chart-subtitle">Nearly all units at ~100% WI</div>
            <BarChart data={topUnits} xKey="unit" yKey="net" horizontal yFormat={formatInt} color="#1B355C" />
          </div>
        </section>
      )}

      <section className="dashboard-section">
        <div className="charts-grid charts-grid-2">
          <div className="chart-card">
            <div className="chart-title">Operations snapshot</div>
            {ops ? (
              <dl className="detail-list">
                <div>
                  <dt>Field staff</dt>
                  <dd>{ops.fieldStaff ?? NA}</dd>
                </div>
                <div>
                  <dt>Compression</dt>
                  <dd>{ops.compressors ?? NA}</dd>
                </div>
                <div>
                  <dt>SWD</dt>
                  <dd>{ops.swd ?? NA}</dd>
                </div>
                <div>
                  <dt>Facilities</dt>
                  <dd>{ops.batteries ?? NA}</dd>
                </div>
                <div>
                  <dt>Fluids</dt>
                  <dd>{ops.fluids ?? NA}</dd>
                </div>
                <div>
                  <dt>Inactive</dt>
                  <dd>{ops.inactive ?? NA}</dd>
                </div>
              </dl>
            ) : (
              <p className="chart-subtitle">{NA}</p>
            )}
          </div>
          <div className="chart-card">
            <div className="chart-title">Marketing & midstream</div>
            {marketing ? (
              <>
                <dl className="detail-list">
                  <div>
                    <dt>Purchaser</dt>
                    <dd>{marketing.purchaser ?? NA}</dd>
                  </div>
                  <div>
                    <dt>Gathering</dt>
                    <dd>{marketing.gathering ?? NA}</dd>
                  </div>
                  <div>
                    <dt>Boswell</dt>
                    <dd>{marketing.boswell ?? NA}</dd>
                  </div>
                  <div>
                    <dt>Bald Prairie</dt>
                    <dd>{marketing.baldPrairie ?? NA}</dd>
                  </div>
                  <div>
                    <dt>Transport</dt>
                    <dd>{marketing.transport ?? NA}</dd>
                  </div>
                </dl>
                <div className="section-title" style={{ marginTop: 16, fontSize: '0.95rem' }}>
                  Fee stack highlights
                </div>
                <ul className="bullet-list">
                  {(marketing.feeNotes || []).map((n) => (
                    <li key={n}>{n}</li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="chart-subtitle">{NA}</p>
            )}
          </div>
        </div>
      </section>

      <section className="dashboard-section">
        <div className="section-title">Full acreage rollup</div>
        <DataTable
          columns={[
            { key: 'unit', label: 'Unit / Tract' },
            {
              key: 'gross',
              label: 'Gross acres',
              format: (v) => v.toFixed(1),
              align: 'right',
            },
            {
              key: 'net',
              label: 'Net acres',
              format: (v) => v.toFixed(1),
              align: 'right',
            },
          ]}
          data={units}
          defaultSort="net"
          maxHeight={400}
        />
      </section>
    </div>
  );
}
